import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import './index.css';
// Vóór initializeApp(), zodat i18next klaar is voordat het eerste component rendert.
import './i18n';
import { initThema } from './thema';
import App from './App';
import { CrashScreen } from './components/CrashScreen';
import { initNativeShell } from './services/nativeBootstrap';
import reportWebVitals from './reportWebVitals';

Sentry.init({
  dsn: "https://40845b432e97d76977a4e191ddedb1b8@o4509339959427072.ingest.de.sentry.io/4509939116343376",
  // Uit: dit zou het IP-adres van de coach meesturen. Dat is een persoonsgegeven dat we bij
  // een crash in een opstellingenapp toch niet gebruiken, en zonder blijft de App Privacy-
  // opgave "Crash Data, niet aan de gebruiker gekoppeld". Zie frontend/public/privacy.html.
  sendDefaultPii: false,
  // Fouten uit scripts die niet van ons zijn. De in-app browsers van Facebook
  // en Instagram (iabjs://) en browserextensies injecteren eigen JS in de
  // pagina; hun crashes komen via window.onerror bij ons binnen.
  denyUrls: [
    /^iabjs:\/\//,
    /^chrome(-extension)?:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(extension|web-extension):\/\//i,
    /extensions\//i,
  ],
  // Vangnet voor extensiefouten die denyUrls mist: als een content script vanuit
  // een anonieme eval of een injected inline <script> gooit, staat onze eigen
  // pagina-URL in de stacktrace en glipt de fout er alsnog doorheen. Dit zijn
  // meldingen van de WebExtensions-API zelf; die kan onze code niet aanroepen.
  ignoreErrors: [
    /Invalid call to runtime\.sendMessage/i,
    /Extension context invalidated/i,
    /Could not establish connection\. Receiving end does not exist/i,
    /The message port closed before a response was received/i,
  ],
  // Tracing. De React Router-variant in plaats van de kale browserTracingIntegration,
  // anders wordt elke /team/:publicId/:slug een eigen transactienaam en is het
  // overzicht in Sentry onbruikbaar. Vereist withSentryReactRouterV7Routing in App.tsx.
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 0.1,
  // Bewust leeg: hiermee stuurt de SDK gegarandeerd geen sentry-trace/baggage
  // headers mee. De backend heeft een expliciete allowedHeaders-whitelist
  // (backend/src/main.ts) en zou de preflight op onbekende headers laten
  // stranden -- dan breekt elke API-call. Bovendien draait er geen Sentry in de
  // backend, dus distributed tracing zou toch nergens op aansluiten. Wil je dat
  // later wel: eerst 'sentry-trace' en 'baggage' aan allowedHeaders toevoegen,
  // die backend uitrollen, en pas daarna hier de API-origin invullen.
  tracePropagationTargets: [],
});

// Wait for the root element to be available
const waitForRoot = () => {
  const rootElement = document.getElementById('root');
  if (rootElement) {
    return rootElement;
  }
  
  // If root doesn't exist, wait a bit and try again
  return new Promise<HTMLElement>((resolve) => {
    const checkForRoot = () => {
      const element = document.getElementById('root');
      if (element) {
        resolve(element);
      } else {
        setTimeout(checkForRoot, 10);
      }
    };
    checkForRoot();
  });
};

// Initialize React app
const initializeApp = async () => {
  const rootElement = await waitForRoot();

  // Ná waitForRoot en niet ervoor. Op de marketingpagina ('/' in de browser) maakt
  // public/index.html bewust geen #root aan, dus daar komt deze regel nooit langs - en dat
  // is precies de bedoeling: die pagina heeft een eigen, handgeschreven stijlblok dat maar
  // half meekantelt, en wordt daarom ook door het voorpaint-script overgeslagen. Stond dit
  // ervóór, dan zou een bezoeker die eerder donker koos alsnog een half omgekleurde
  // landingspagina krijgen. Het thema hoort bij de app, en de app is waar #root staat.
  //
  // Het voorpaint-script heeft data-thema dan al gezet; dit neemt het over en hangt de
  // luisteraars op, zodat een systeemwissel mid-sessie doorwerkt.
  initThema();

  const root = ReactDOM.createRoot(rootElement);
  
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<CrashScreen />}>
        <App />
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );

  // Na render, zodat de splash pas verdwijnt als er ook echt iets te zien is.
  // Doet niets op het web.
  void initNativeShell();
};

initializeApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
